import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { trackSignup } from "@/lib/analytics";
import { LOGO, IMAGES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Seo } from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";

const C = {
  fr: { back: "Retour à l'accueil", title: "Créer un compte", subtitle: "Commencez votre projet d'immigration.",
    referral: "Vous avez été parrainé — bienvenue ! Code :", name: "Nom complet", nameP: "Votre nom",
    email: "Email", phone: "Téléphone", password: "Mot de passe", passwordP: "Min. 6 caractères",
    country: "Pays de résidence", countryP: "Sélectionnez votre pays",
    submit: "Créer mon compte", submitting: "Création…", have: "Déjà un compte ?", login: "Se connecter",
    success: "Compte créé avec succès !", sideTitle: "Rejoignez-nous",
    consent: "Je reconnais avoir lu et compris les conditions d'éligibilité, les avertissements importants et les informations relatives aux visas. Je comprends que la décision finale appartient exclusivement aux autorités d'immigration compétentes (IRCC Canada ou Department of Home Affairs Australie) et que Work & Travel Expert ne garantit pas l'obtention d'un visa.",
    consentRequired: "Veuillez confirmer avoir lu et compris les conditions d'éligibilité.",
    sideText: "Un accompagnement premium pour concrétiser votre projet en Australie ou au Canada." },
  en: { back: "Back to home", title: "Create an account", subtitle: "Start your immigration project.",
    referral: "You have been referred — welcome! Code:", name: "Full name", nameP: "Your name",
    email: "Email", phone: "Phone", password: "Password", passwordP: "Min. 6 characters",
    country: "Country of residence", countryP: "Select your country",
    submit: "Create my account", submitting: "Creating…", have: "Already have an account?", login: "Log in",
    success: "Account created successfully!", sideTitle: "Join us",
    sideText: "Premium support to make your project in Australia or Canada a reality." },
};

const COUNTRIES = {
  fr: [
    { v: "France", l: "France" },
    { v: "Belgique", l: "Belgique" },
    { v: "Suisse", l: "Suisse" },
    { v: "Luxembourg", l: "Luxembourg" },
    { v: "Autre", l: "Autre pays" },
  ],
  en: [
    { v: "France", l: "France" },
    { v: "Belgique", l: "Belgium" },
    { v: "Suisse", l: "Switzerland" },
    { v: "Luxembourg", l: "Luxembourg" },
    { v: "Autre", l: "Other country" },
  ],
};

export default function Register() {
  const { lang } = useLang();
  const c = C[lang];
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const destination = params.get("destination");
  const destLabel = destination === "australie" ? "🇦🇺 Australie" : destination === "canada" ? "🇨🇦 Canada" : null;
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "", password: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error(c.consentRequired); return; }
    setLoading(true);
    try {
      await register({ ...form, referral_code: ref || undefined });
      trackSignup("email");
      toast.success(c.success);
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiErrorMessage(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-screen grid lg:grid-cols-2">
      <Seo title="Créer un compte | Work & Travel Expert" description="Créez votre compte Work & Travel Expert et démarrez votre projet d'immigration vers l'Australie ou le Canada avec un accompagnement premium." path="/inscription" noindex />
      <div className="flex flex-col justify-center px-6 sm:px-16 py-12 order-2 lg:order-1">
        <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors mb-10"><ArrowLeft size={16} /> {c.back}</Link>
        <img src={LOGO} alt="Logo" className="h-16 w-16 object-contain" />
        <h1 className="font-heading text-4xl text-navy mt-6">{c.title}</h1>
        <p className="text-muted-foreground mt-2">{c.subtitle}</p>
        {ref && <p data-testid="referral-banner" className="mt-3 text-sm bg-gold/10 text-navy border border-gold/40 px-4 py-2">{c.referral} <strong>{ref}</strong></p>}
        {destLabel && <p data-testid="destination-banner" className="mt-3 inline-flex items-center gap-2 text-sm bg-navy text-white px-4 py-2"><span className="text-white/70">{lang === "fr" ? "Destination choisie :" : "Chosen destination:"}</span> <strong>{destLabel}</strong></p>}

        <form onSubmit={submit} className="mt-8 space-y-5 max-w-md">
          <div><Label className="text-navy">{c.name}</Label>
            <Input required data-testid="register-name" value={form.name} onChange={upd("name")} className="mt-2 rounded-none" placeholder={c.nameP} /></div>
          <div><Label className="text-navy">{c.email}</Label>
            <Input required type="email" data-testid="register-email" value={form.email} onChange={upd("email")} className="mt-2 rounded-none" placeholder="vous@email.com" /></div>
          <div><Label className="text-navy">{c.phone}</Label>
            <Input data-testid="register-phone" value={form.phone} onChange={upd("phone")} className="mt-2 rounded-none" placeholder="+33 …" /></div>
          <div><Label className="text-navy">{c.country}</Label>
            <select required data-testid="register-country" value={form.country} onChange={upd("country")}
              className="mt-2 w-full h-10 border border-input bg-background px-3 py-2 text-sm rounded-none outline-none focus:border-gold transition-colors">
              <option value="" disabled>{c.countryP}</option>
              {COUNTRIES[lang].map((o) => (<option key={o.v} value={o.v}>{o.l}</option>))}
            </select></div>
          <div><Label className="text-navy">{c.password}</Label>
            <Input required type="password" minLength={6} data-testid="register-password" value={form.password} onChange={upd("password")} className="mt-2 rounded-none" placeholder={c.passwordP} /></div>
          <label className="flex items-start gap-3 cursor-pointer text-sm text-navy bg-[#F8F9FA] border border-border p-4">
            <input type="checkbox" data-testid="register-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-4 w-4 accent-gold shrink-0" />
            <span className="leading-relaxed">{c.consent}</span>
          </label>
          <button type="submit" disabled={loading || !consent} data-testid="register-submit" className="w-full bg-gold hover:bg-gold-hover text-white py-4 font-medium transition-colors disabled:opacity-60">
            {loading ? c.submitting : c.submit}
          </button>
        </form>
        <p className="mt-6 text-sm text-muted-foreground">{c.have} <Link to="/connexion" data-testid="go-login" className="text-gold font-medium hover:underline">{c.login}</Link></p>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={IMAGES.australia} alt="Australie" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy/70" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <h2 className="font-heading text-4xl">{c.sideTitle}</h2>
          <p className="mt-3 text-white/75 max-w-sm">{c.sideText}</p>
        </div>
      </div>
    </div>
  );
}
